import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import {
  emptyState,
  errorState,
  isResultState,
  loadingState,
  resultState,
} from '../../@core/fetch-state.utils';

import { OrdersActions, updateOrderInStore } from './orders.actions';
import { singleOrderDetails } from './orders.model';
import { OrdersState } from './orders.state.interface';

export const ordersesFeatureKey = 'orderses';

export const adapter: EntityAdapter<singleOrderDetails> = createEntityAdapter<singleOrderDetails>();

export const initialState: OrdersState = adapter.getInitialState({
  ordersList: emptyState(),
  order: emptyState(),
  loading: false,
  error: null,
  orderAssignees: emptyState(),
  orderStatuses: emptyState(),
  orderClients: emptyState(),
  addNoteLoading: false,
  materialsCategories: [],
  materialsCategoriesLoading: false,
  materialsCategoriesError: null,
  materialsStatus: [],
  materialsStatusLoading: false,
  materialsStatusError: null,
  packingSlipCategories: [],
  packingSlipCategoriesLoading: false,
  packingSlipCategoriesError: null,
  totalCount: 0,
  lastAction: null,
  myOrdersList: emptyState() || null,
  myOrdersListLength: emptyState() || null,
  myOrdersTotalPrice: emptyState() || null,
  pastDueOrders: emptyState() || null,
  pastDueOrdersLength: emptyState() || null,
  pastDueOrdersTotalPrice: emptyState() || null,
  ordersDueTodayTotalPrice: emptyState() || null,
  ordersDueToday: emptyState() || null,
  ordersDueTodayLength: emptyState() || null,
  lists: [],
  listsLoading: false,
  listsError: null,
  listsTotalCount: 0,
  customerCodes: emptyState(),
  singleList: null,
  singleListLoading: false,
  singleListError: null,
  exportDetails: null,
  exportDetailsLoading: false,
  exportDetailsError: null,
  exportOrderLoading: false,
  exportOrderError: null,
  exportOrderSuccessResponse: null,
  priceTotal: 0,
  priceTotalNotShipped: 0,
  exportOrdersBundleLoading: false,
  exportOrdersBundleError: null,
  exportOrdersBundleResponse: null,
  createdOrder: emptyState(),
  updatedOrder: emptyState(),
  deleteList: emptyState(),
});

export const ordersReducer = createReducer(
  initialState,
  // Entity actions
  // on(OrdersActions.addOrders, (state, action) => adapter.addOne(action.orders, state)),
  // on(OrdersActions.upsertOrders, (state, action) => adapter.upsertOne(action.orders, state)),
  // on(OrdersActions.addOrderss, (state, action) => adapter.addMany(action.orderss, state)),
  // on(OrdersActions.upsertOrderss, (state, action) => adapter.upsertMany(action.orderss, state)),
  // on(OrdersActions.updateOrders, (state, action) => adapter.updateOne(action.orders, state)),
  // on(OrdersActions.updateOrderss, (state, action) => adapter.updateMany(action.orderss, state)),
  // on(OrdersActions.deleteOrders, (state, action) => adapter.removeOne(action.id, state)),
  // on(OrdersActions.deleteOrderss, (state, action) => adapter.removeMany(action.ids, state)),
  // on(OrdersActions.loadOrderss, (state, action) => adapter.setAll(action.orderss, state)),
  // on(OrdersActions.clearOrderss, (state) => adapter.removeAll(state)),

  // Order actions
  on(OrdersActions.loadOrder, (state) => ({
    ...state,
    loading: true,
    error: null,
    lastAction: 'Load Order',
    order: loadingState(),
  })),
  on(OrdersActions.loadOrderSuccess, (state, { order }) => ({
    ...state,
    order: resultState(order),
    loading: false,
    error: null,
    lastAction: 'Load Order Success',
  })),
  on(OrdersActions.loadOrderFailure, (state, { error }) => ({
    ...state,
    loading: false,
    order: errorState([error]),
    error,
    lastAction: 'Load Order Failure',
  })),

  // Archive Order
  on(OrdersActions.archiveOrder, (state) => ({
    ...state,
    loading: true,
    error: null,
    lastAction: 'Archive Order',
  })),
  on(OrdersActions.archiveOrderSuccess, (state, { orderId, isArchived }) => ({
    ...state,
    loading: false,
    error: null,
    lastAction: 'Archive Order Success',
  })),
  on(OrdersActions.archiveOrderFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    lastAction: 'Archive Order Failure',
  })),

  // Delete Order
  on(OrdersActions.deleteOrder, (state) => ({
    ...state,
    loading: true,
    error: null,
    lastAction: 'Delete Order',
  })),
  on(OrdersActions.deleteOrderSuccess, (state, { orderId }) => ({
    ...state,
    loading: false,
    error: null,
    lastAction: 'Delete Order Success',
  })),
  on(OrdersActions.deleteOrderFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    lastAction: 'Delete Order Failure',
  })),

  // Order Assignees
  on(OrdersActions.loadOrderAssignees, (state) => ({
    ...state,
    orderAssignees: loadingState(),
  })),
  on(OrdersActions.loadOrderAssigneesSuccess, (state, { assignees }) => ({
    ...state,
    orderAssignees: resultState(assignees),
  })),
  on(OrdersActions.loadOrderAssigneesFailure, (state, { error }) => ({
    ...state,
    orderAssignees: errorState([error]),
  })),

  // Order Statuses
  on(OrdersActions.loadOrderStatuses, (state) => ({
    ...state,
    orderStatuses: loadingState(),
  })),
  on(OrdersActions.loadOrderStatusesSuccess, (state, { statuses }) => ({
    ...state,
    orderStatuses: resultState(statuses),
  })),
  on(OrdersActions.loadOrderStatusesFailure, (state, { error }) => ({
    ...state,
    orderStatuses: errorState([error]),
  })),

  // Order Clients
  on(OrdersActions.loadOrderClients, (state) => ({
    ...state,
    orderClients: loadingState(),
  })),
  on(OrdersActions.loadOrderClientsSuccess, (state, { clients }) => ({
    ...state,
    orderClients: resultState(clients),
  })),
  on(OrdersActions.loadOrderClientsFailure, (state, { error }) => ({
    ...state,
    orderClients: errorState([error]),
  })),

  // Add Order Note Loading
  on(OrdersActions.addOrderNote, (state) => ({
    ...state,
    addNoteLoading: true,
  })),
  on(OrdersActions.addOrderNoteSuccess, (state, { orderId, noteObj }) => {
    if (isResultState(state.order) && state.order.data && state.order.data.id === orderId) {
      const currentOrder = state.order.data;
      const updatedOrder = {
        ...currentOrder,
        notes: [noteObj, ...(currentOrder.notes || [])],
      };
      return {
        ...state,
        addNoteLoading: false,
        order: resultState(updatedOrder),
      };
    }
    return {
      ...state,
      addNoteLoading: false,
    };
  }),
  on(OrdersActions.addOrderNoteFailure, (state) => ({
    ...state,
    addNoteLoading: false,
  })),

  // Materials Categories
  on(OrdersActions.loadOrderMaterialsCategories, (state) => ({
    ...state,
    materialsCategoriesLoading: true,
    materialsCategoriesError: null,
  })),
  on(OrdersActions.loadOrderMaterialsCategoriesSuccess, (state, { categories }) => ({
    ...state,
    materialsCategories: categories,
    materialsCategoriesLoading: false,
    materialsCategoriesError: null,
  })),
  on(OrdersActions.loadOrderMaterialsCategoriesFailure, (state, { error }) => ({
    ...state,
    materialsCategoriesLoading: false,
    materialsCategoriesError: error,
  })),

  // Materials Status
  on(OrdersActions.loadOrderMaterialsStatus, (state) => ({
    ...state,
    materialsStatusLoading: true,
    materialsStatusError: null,
  })),
  on(OrdersActions.loadOrderMaterialsStatusSuccess, (state, { statuses }) => ({
    ...state,
    materialsStatus: statuses,
    materialsStatusLoading: false,
    materialsStatusError: null,
  })),
  on(OrdersActions.loadOrderMaterialsStatusFailure, (state, { error }) => ({
    ...state,
    materialsStatusLoading: false,
    materialsStatusError: error,
  })),

  // Packing Slip Categories
  on(OrdersActions.loadPackingSlipCategories, (state) => ({
    ...state,
    packingSlipCategoriesLoading: true,
    packingSlipCategoriesError: null,
  })),
  on(OrdersActions.loadPackingSlipCategoriesSuccess, (state, { categories }) => ({
    ...state,
    packingSlipCategories: categories,
    packingSlipCategoriesLoading: false,
    packingSlipCategoriesError: null,
  })),
  on(OrdersActions.loadPackingSlipCategoriesFailure, (state, { error }) => ({
    ...state,
    packingSlipCategoriesLoading: false,
    packingSlipCategoriesError: error,
  })),

  // Save Order
  on(OrdersActions.saveOrder, (state) => ({
    ...state,
    loading: true,
    error: null,
    updatedOrder: loadingState(),
  })),
  on(OrdersActions.saveOrderSuccess, (state, { order }) => ({
    ...state,
    updatedOrder: resultState(order),
    loading: false,
    error: null,
  })),
  on(OrdersActions.saveOrderFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    updatedOrder: errorState([error]),
  })),

  // Load Orders List (for table)
  on(OrdersActions.loadOrdersList, (state) => ({
    ...state,
    loading: true,
    error: null,
    ordersList: loadingState(),
    priceTotal: 0,
    priceTotalNotShipped: 0,
  })),
  on(
    OrdersActions.loadOrdersListSuccess,
    (state, { orders, totalCount, priceTotal, priceTotalNotShipped }) => ({
      ...state,
      ordersList: resultState(orders),
      priceTotal,
      priceTotalNotShipped,
      loading: false,
      error: null,
      totalCount,
    }),
  ),
  on(OrdersActions.loadOrdersListFailure, (state, { error }) => ({
    ...state,
    ordersList: errorState([error]),
    loading: false,
    error,
    priceTotal: 0,
    priceTotalNotShipped: 0,
  })),

  // Load My Orders
  on(OrdersActions.loadMyOrders, (state) => ({
    ...state,
    loading: true,
    error: null,
    myOrdersList: loadingState(),
    myOrdersListLength: loadingState(),
    myOrdersTotalPrice: loadingState(),
  })),
  on(OrdersActions.loadMyOrdersSuccess, (state, { orders, totalCount, totalPrice }) => ({
    ...state,
    myOrdersList: resultState(orders),
    myOrdersListLength: resultState(totalCount),
    myOrdersTotalPrice: resultState(totalPrice),
    loading: false,
    error: null,
    totalCount,
  })),
  on(OrdersActions.loadMyOrdersFailure, (state, { error }) => ({
    ...state,
    myOrdersList: errorState([error]),
    myOrdersListLength: errorState([error]),
    myOrdersTotalPrice: errorState([error]),
    loading: false,
    error,
  })),

  // Load Past Due Orders
  on(OrdersActions.loadPastDueOrders, (state) => ({
    ...state,
    loading: true,
    error: null,
    pastDueOrders: loadingState(),
    pastDueOrdersLength: loadingState(),
    pastDueOrdersTotalPrice: loadingState(),
  })),
  on(OrdersActions.loadPastDueOrdersSuccess, (state, { orders, totalCount, totalPrice }) => ({
    ...state,
    pastDueOrders: resultState(orders),
    pastDueOrdersLength: resultState(totalCount),
    pastDueOrdersTotalPrice: resultState(totalPrice),
    loading: false,
    error: null,
    totalCount,
  })),
  on(OrdersActions.loadPastDueOrdersFailure, (state, { error }) => ({
    ...state,
    pastDueOrders: errorState([error]),
    pastDueOrdersLength: errorState([error]),
    pastDueOrdersTotalPrice: errorState([error]),
    loading: false,
    error,
  })),

  // Load Orders Due Today
  on(OrdersActions.loadOrdersDueToday, (state) => ({
    ...state,
    loading: true,
    error: null,
    ordersDueToday: loadingState(),
    ordersDueTodayLength: loadingState(),
    ordersDueTodayTotalPrice: loadingState(),
  })),
  on(OrdersActions.loadOrdersDueTodaySuccess, (state, { orders, totalCount, totalPrice }) => ({
    ...state,
    ordersDueToday: resultState(orders),
    ordersDueTodayLength: resultState(totalCount),
    ordersDueTodayTotalPrice: resultState(totalPrice),
    loading: false,
    error: null,
    totalCount,
  })),
  on(OrdersActions.loadOrdersDueTodayFailure, (state, { error }) => ({
    ...state,
    ordersDueToday: errorState([error]),
    ordersDueTodayLength: errorState([error]),
    ordersDueTodayTotalPrice: errorState([error]),
    loading: false,
    error,
  })),

  // Update Order in Store
  on(updateOrderInStore, (state, { order }) => ({
    ...state,
    order: resultState(order),
    loading: false,
    error: null,
  })),

  // Lists
  on(OrdersActions.loadLists, (state) => ({
    ...state,
    listsLoading: true,
    listsError: null,
  })),
  on(OrdersActions.loadListsSuccess, (state, { payload }) => ({
    ...state,
    lists: payload.lists,
    listsTotalCount: payload.totalCount,
    listsLoading: false,
    listsError: null,
  })),
  on(OrdersActions.loadListsFailure, (state, { error }) => ({
    ...state,
    listsLoading: false,
    listsError: error,
  })),

  // Customer Codes
  on(OrdersActions.loadCustomerCodes, (state) => ({
    ...state,
    customerCodes: loadingState(),
  })),
  on(OrdersActions.loadCustomerCodesSuccess, (state, { customerCodes }) => ({
    ...state,
    customerCodes: resultState(customerCodes),
  })),
  on(OrdersActions.loadCustomerCodesFailure, (state, { error }) => ({
    ...state,
    customerCodes: errorState([error]),
  })),

  // Single List
  on(OrdersActions.loadSingleList, (state) => ({
    ...state,
    singleList: null,
    singleListLoading: true,
    singleListError: null,
  })),
  on(OrdersActions.loadSingleListSuccess, (state, { singleList }) => ({
    ...state,
    singleList,
    singleListLoading: false,
    singleListError: null,
  })),
  on(OrdersActions.loadSingleListFailure, (state, { error }) => ({
    ...state,
    singleList: null,
    singleListLoading: false,
    singleListError: error,
  })),

  // Create Single List
  on(OrdersActions.createSingleList, (state) => ({
    ...state,
    singleListLoading: true,
    singleListError: null,
  })),
  on(OrdersActions.createSingleListSuccess, (state, { response }) => ({
    ...state,
    singleListLoading: false,
    singleListError: null,
  })),
  on(OrdersActions.createSingleListFailure, (state, { error }) => ({
    ...state,
    singleListLoading: false,
    singleListError: error,
  })),

  // Update Single List
  on(OrdersActions.updateSingleList, (state) => ({
    ...state,
    singleListLoading: true,
    singleListError: null,
  })),
  on(OrdersActions.updateSingleListSuccess, (state, { response }) => ({
    ...state,
    singleListLoading: false,
    singleListError: null,
  })),
  on(OrdersActions.updateSingleListFailure, (state, { error }) => ({
    ...state,
    singleListLoading: false,
    singleListError: error,
  })),

  // Delete List
  on(OrdersActions.deleteList, (state) => ({
    ...state,
    loading: true,
    error: null,
    lastAction: 'Delete List',
    deleteList: loadingState(),
  })),
  on(OrdersActions.deleteListSuccess, (state, { id }) => ({
    ...state,
    loading: false,
    error: null,
    lastAction: 'Delete List Success',
    deleteList: resultState(id),
  })),
  on(OrdersActions.deleteListFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    lastAction: 'Delete List Failure',
    deleteList: errorState([error]),
  })),

  // Export List
  on(OrdersActions.exportList, (state) => ({
    ...state,
    loading: true,
    error: null,
    lastAction: 'Export List',
  })),
  on(OrdersActions.exportListSuccess, (state, { id }) => ({
    ...state,
    loading: false,
    error: null,
    lastAction: 'Export List Success',
  })),
  on(OrdersActions.exportListFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    lastAction: 'Export List Failure',
  })),

  // Load Export Details
  on(OrdersActions.loadExportDetails, (state) => ({
    ...state,
    exportDetailsLoading: true,
    exportDetailsError: null,
  })),
  on(OrdersActions.loadExportDetailsSuccess, (state, { exportDetails }) => ({
    ...state,
    exportDetails,
    exportDetailsLoading: false,
    exportDetailsError: null,
  })),
  on(OrdersActions.loadExportDetailsFailure, (state, { error }) => ({
    ...state,
    exportDetailsLoading: false,
    exportDetailsError: error,
  })),

  // Export Order
  on(OrdersActions.exportOrder, (state) => ({
    ...state,
    exportOrderLoading: true,
    exportOrderError: null,
    exportOrderSuccessResponse: null,
  })),
  on(OrdersActions.exportOrderSuccess, (state, { response }) => ({
    ...state,
    exportOrderLoading: false,
    exportOrderError: null,
    exportOrderSuccessResponse: null,
    lastAction: 'Export Order Success',
  })),
  on(OrdersActions.exportOrderFailure, (state, { error }) => ({
    ...state,
    exportOrderLoading: false,
    exportOrderError: error,
    exportOrderSuccessResponse: null,
    lastAction: 'Export Order Failure',
  })),

  // Export Orders Bundle
  on(OrdersActions.exportOrdersBundle, (state) => ({
    ...state,
    exportOrdersBundleLoading: true,
    exportOrdersBundleError: null,
    exportOrdersBundleResponse: null,
  })),
  on(OrdersActions.exportOrdersBundleSuccess, (state, { response }) => ({
    ...state,
    exportOrdersBundleLoading: false,
    exportOrdersBundleError: null,
    exportOrdersBundleResponse: response,
  })),
  on(OrdersActions.exportOrdersBundleFailure, (state, { error }) => ({
    ...state,
    exportOrdersBundleLoading: false,
    exportOrdersBundleError: error,
    exportOrdersBundleResponse: null,
  })),

  // Create Order
  on(OrdersActions.createOrder, (state) => ({
    ...state,
    createdOrder: loadingState(),
  })),
  on(OrdersActions.createOrderSuccess, (state, { order }) => ({
    ...state,
    createdOrder: resultState(order),
  })),
  on(OrdersActions.createOrderFailure, (state, { error }) => ({
    ...state,
    createdOrder: errorState([error]),
  })),
);

// export const ordersesFeature = createFeature({
//   name: ordersesFeatureKey,
//   reducer: ordersReducer,
//   extraSelectors: ({ selectOrdersesState }) => ({
//     ...adapter.getSelectors(selectOrdersesState),
//   }),
// });

// export const { selectIds, selectEntities, selectAll, selectTotal } = ordersesFeature;
